import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { cartoTileUrl } from './cartoTiles';

const TILE = {
  url: cartoTileUrl('rastertiles/voyager'),
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
};

function pinIcon(color) {
  return L.divIcon({
    className: 'actuacion-map-pin',
    html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  const key = points?.map((p) => p.join(',')).join('|') || '';
  useEffect(() => {
    if (!points?.length) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    const b = L.latLngBounds(points);
    map.fitBounds(b, { padding: [28, 28], maxZoom: 15 });
  }, [map, key]);
  return null;
}

/**
 * Mapa compacto: inicio (morado), fin/llegada (rojo), ruta entre ambos, opcional autobús (azul).
 */
export default function ActuacionMapPreview({
  lat,
  lng,
  latFin,
  lngFin,
  latAutobus,
  lngAutobus,
  height = '220px',
}) {
  const start = useMemo(() => {
    if (lat == null || lng == null) return null;
    const la = Number(lat);
    const lo = Number(lng);
    if (Number.isNaN(la) || Number.isNaN(lo)) return null;
    return [la, lo];
  }, [lat, lng]);

  const end = useMemo(() => {
    if (latFin == null || lngFin == null) return null;
    const la = Number(latFin);
    const lo = Number(lngFin);
    if (Number.isNaN(la) || Number.isNaN(lo)) return null;
    return [la, lo];
  }, [latFin, lngFin]);

  const bus = useMemo(() => {
    if (latAutobus == null || lngAutobus == null) return null;
    const la = Number(latAutobus);
    const lo = Number(lngAutobus);
    if (Number.isNaN(la) || Number.isNaN(lo)) return null;
    return [la, lo];
  }, [latAutobus, lngAutobus]);

  const center = start || end || bus;
  const points = [start, end, bus].filter(Boolean);
  const mapKey = `${start?.join(',') || ''}-${end?.join(',') || ''}-${bus?.join(',') || ''}`;

  if (!center) return null;

  return (
    <MapContainer
      key={mapKey}
      center={center}
      zoom={14}
      style={{ height, width: '100%', borderRadius: 8 }}
      scrollWheelZoom={false}
    >
      <TileLayer url={TILE.url} attribution={TILE.attribution} />
      <FitBounds points={points} />
      {start ? (
        <Marker position={start} icon={pinIcon('#7c3aed')}>
          <Popup>Inicio / salida</Popup>
        </Marker>
      ) : null}
      {end ? (
        <Marker position={end} icon={pinIcon('#dc2626')}>
          <Popup>Fin / llegada</Popup>
        </Marker>
      ) : null}
      {bus ? (
        <Marker position={bus} icon={pinIcon('#0369a1')}>
          <Popup>Autobús (recogida)</Popup>
        </Marker>
      ) : null}
      {start && end ? (
        <Polyline positions={[start, end]} pathOptions={{ color: '#7c3aed', weight: 3, opacity: 0.8, dashArray: '6 10' }} />
      ) : null}
    </MapContainer>
  );
}
